import React, { useEffect, useMemo } from "react";
import * as datefns from "date-fns";
import { isScheduleGridVisible } from "./RpaReservationStepScheduleGrid";

/** @typedef {import('../../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */

/**
 * @typedef {object} RpaReservationStepDateProps
 * @property {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @property {Array<number>} selectedResourceIds - Ressources sélectionnées
 * @property {?string} selectedDateStr - Date sélectionnée
 * @property {?string} initialDayDateStr - Date initiale souhaitée
 * @property {function(?string):void} onDateChange - Sélection d'une date
 */

/**
 * Étape de sélection de la date
 * @param {RpaReservationStepDateProps} props
 */
export default function RpaReservationStepDate({ planningDays, selectedResourceIds, selectedDateStr, initialDayDateStr, onDateChange }) {
  const availableDates = useMemo(() => {
    if (!selectedResourceIds.length) {
      return [];
    }

    return planningDays.reduce((dateList, day) => {
      const hasVisibleScheduleGrid = day.rpaResources.some(resource => selectedResourceIds.includes(Number(resource.id)) && resource.scheduleGridsOfCurrentDay.some(isScheduleGridVisible));

      if (hasVisibleScheduleGrid) {
        dateList.push(day.dayStr);
      }

      return dateList;
    }, /** @type {Array<string>} */ ([]));
  }, [planningDays, selectedResourceIds]);
  const selectedDateIndex = availableDates.indexOf(selectedDateStr);
  const hasPreviousDate = selectedDateIndex > 0;
  const hasNextDate = selectedDateIndex >= 0 && selectedDateIndex < availableDates.length - 1;

  useEffect(() => {
    if (!selectedResourceIds.length) {
      return;
    }

    if (selectedDateStr && availableDates.includes(selectedDateStr)) {
      return;
    }

    onDateChange(initialDayDateStr && availableDates.includes(initialDayDateStr) ? initialDayDateStr : availableDates[0] || null);
  }, [availableDates, initialDayDateStr, onDateChange, selectedDateStr, selectedResourceIds]);

  if (!selectedResourceIds.length) {
    return null;
  }

  function selectPreviousDate() {
    if (hasPreviousDate) {
      onDateChange(availableDates[selectedDateIndex - 1]);
    }
  }

  function selectNextDate() {
    if (hasNextDate) {
      onDateChange(availableDates[selectedDateIndex + 1]);
    }
  }

  return (
    <section>
      <div className="space--8"></div>
      <h3 className="title title--small title--no-margin">Date</h3>
      <div className="space--8"></div>
      {availableDates.length ? (
        <div className="flexline flexline--margin flexline--align-vz flexline--no-mobile">
          <button type="button" className="btn" aria-label="Date précédente" disabled={!hasPreviousDate} onClick={selectPreviousDate}>
            <span className="material-symbols-outlined">chevron_left</span>
            Précédent
          </button>
          <div className="input-group input-group--no-margin" style={{ flex: 1 }}>
            <select aria-label="Date de votre réservation" className="input-group__input" value={selectedDateStr || ""} onChange={event => onDateChange(event.target.value)}>
              {availableDates.map(dateStr => (
                <option key={dateStr} value={dateStr}>
                  {formatReservationDate(dateStr)}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn" aria-label="Date suivante" disabled={!hasNextDate} onClick={selectNextDate}>
            Suivant
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      ) : (
        <p>Aucune date de réservation n'est disponible.</p>
      )}
    </section>
  );
}

/**
 * Formate une date de réservation avec son jour de la semaine
 * TU TODO 🔨
 * @param {string} dateStr - Date au format dd/MM/yyyy
 * @returns {string}
 */
function formatReservationDate(dateStr) {
  const date = datefns.parse(dateStr, "dd/MM/yyyy", new Date());
  const formattedDate = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);

  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
}
